import { useEffect, useState } from 'react';
import { errorMessage } from '../../../lib/errorMessage';
import { ChevronRight, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Drawer } from '../../../components/Drawer/Drawer';
import { EntityIdBadge } from '../../../components/EntityIdBadge';
import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/cn';
import { formatHebrewRelative } from '../../../lib/relativeTime';
import {
  ContactForm,
  ContactsList,
  useContactsByCompany,
  useSoftDeleteContact,
  useUpsertContact,
  type CompanyContactRow,
  type ContactInput,
} from '../contacts';
import type { CompanyInput, CompanyRow } from './companies.schema';
import {
  useCompanyActivity,
  useCompanyDeals,
  useSoftDeleteCompany,
  useUpsertCompany,
  type CompanyActivityRow,
  type CompanyDealRow,
} from './companiesQueries';
import { CompanyForm } from './CompanyForm';
import styles from './companies.module.css';
import contactStyles from '../contacts/contacts.module.css';

const COMPANY_FORM_ID = 'company-form';
const CONTACT_FORM_ID = 'contact-form';

type DrawerTab = 'overview' | 'contacts' | 'deals' | 'activity';
type ContactView = 'list' | 'form';

const ACTION_LABELS: Record<string, string> = {
  'companies.insert': 'נוצרה חברה',
  'companies.update': 'עודכנה חברה',
  'companies.delete': 'נמחקה חברה',
  'contacts.insert': 'נוצר איש קשר',
  'contacts.update': 'עודכן איש קשר',
  'contacts.delete': 'נמחק איש קשר',
  'deals.insert': 'נוצר דיל',
  'deals.update': 'עודכן דיל',
  'deals.delete': 'נמחק דיל',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function moneyHe(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${String(amount)} ${currency}`;
  }
}

interface KpiStripProps {
  deals: CompanyDealRow[];
  contactsCount: number;
  customerSince: string | null;
}

function KpiStrip({ deals, contactsCount, customerSince }: KpiStripProps) {
  const openDeals = deals.filter((d) => d.value_amount > 0).length;
  const totalValue = deals.reduce((sum, d) => sum + d.value_amount, 0);
  const currency = deals[0]?.value_currency ?? 'ILS';

  return (
    <div className={styles.kpiStrip}>
      <div className={styles.kpiCard}>
        <span className={styles.kpiLabel}>דילים</span>
        <span className={styles.kpiValue}>
          <span className="num">{String(openDeals)}</span>
        </span>
      </div>
      <div className={styles.kpiCard}>
        <span className={styles.kpiLabel}>סה״כ שווי</span>
        <span className={styles.kpiValue} dir="ltr">
          {moneyHe(totalValue, currency)}
        </span>
      </div>
      <div className={styles.kpiCard}>
        <span className={styles.kpiLabel}>אנשי קשר</span>
        <span className={styles.kpiValue}>
          <span className="num">{String(contactsCount)}</span>
        </span>
      </div>
      <div className={styles.kpiCard}>
        <span className={styles.kpiLabel}>לקוח מאז</span>
        <span className={styles.kpiValue}>
          {customerSince ? formatHebrewRelative(customerSince) : '—'}
        </span>
      </div>
    </div>
  );
}

function DealsList({ deals }: { deals: CompanyDealRow[] }) {
  if (deals.length === 0) {
    return <p className={styles.notice}>אין פרויקטים פתוחים ללקוח זה.</p>;
  }
  return (
    <ul className={styles.dealsList}>
      {deals.map((d) => (
        <li key={d.id} className={styles.dealRow}>
          <Link to="/projects" className={styles.dealLink}>
            <span className={styles.dealName}>{d.name}</span>
            <span className={styles.dealStage}>{d.status}</span>
            <span className={styles.dealValue} dir="ltr">
              {moneyHe(d.value_amount, d.value_currency)}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ActivityFeed({ rows, isLoading }: { rows: CompanyActivityRow[]; isLoading: boolean }) {
  if (isLoading) return <p className={styles.notice}>טוען פעילות...</p>;
  if (rows.length === 0) return <p className={styles.notice}>אין פעילות לחברה זו.</p>;
  return (
    <ul className={styles.activityList}>
      {rows.map((row) => (
        <li key={row.id} className={styles.activityRow}>
          <span className={styles.activityLabel}>{actionLabel(row.action)}</span>
          <span className={styles.activityTime}>{formatHebrewRelative(row.created_at)}</span>
        </li>
      ))}
    </ul>
  );
}

interface CompanyDrawerProps {
  open: boolean;
  company: CompanyRow | null;
  orgId: string | null;
  onClose: () => void;
}

export function CompanyDrawer({ open, company, orgId, onClose }: CompanyDrawerProps) {
  const toast = useToast();
  const upsertCompany = useUpsertCompany(orgId);
  const softDeleteCompany = useSoftDeleteCompany(orgId);
  const [freshId, setFreshId] = useState<string | null>(null);
  const effectiveId = company?.id ?? freshId;
  const {
    data: contacts = [],
    isLoading: contactsLoading,
    error: contactsError,
  } = useContactsByCompany(orgId, effectiveId);
  const { data: companyDeals = [] } = useCompanyDeals(orgId, effectiveId);
  const { data: companyActivity = [], isLoading: activityLoading } = useCompanyActivity(
    orgId,
    effectiveId,
  );
  const upsertContact = useUpsertContact(orgId, effectiveId);
  const softDeleteContact = useSoftDeleteContact(effectiveId);
  const [tab, setTab] = useState<DrawerTab>('overview');
  const [contactView, setContactView] = useState<ContactView>('list');
  const [selectedContact, setSelectedContact] = useState<CompanyContactRow | null>(null);
  const isEditing = Boolean(company) || freshId !== null;
  const companyBusy = upsertCompany.isPending || softDeleteCompany.isPending;
  const contactBusy = upsertContact.isPending || softDeleteContact.isPending;
  const customerSince = company?.created_at ?? null;
  const displayId = effectiveId;

  useEffect(() => {
    if (!open) {
      setFreshId(null);
      return;
    }
    setTab('overview');
    setContactView('list');
    setSelectedContact(null);
    setFreshId(null);
  }, [company?.id, open]);

  async function handleSubmit(values: CompanyInput) {
    try {
      const result = await upsertCompany.mutateAsync({ id: company?.id ?? freshId, values });
      if (result.created) {
        // Stay in drawer → pivot to contacts tab + open empty contact form,
        // so adding a customer is one continuous motion.
        setFreshId(result.id);
        setTab('contacts');
        setContactView('form');
        setSelectedContact(null);
        toast.success('החברה נשמרה · הוסף איש קשר ראשון');
      } else {
        toast.success('החברה נשמרה');
        onClose();
      }
    } catch {
      toast.error('לא הצלחנו לשמור · נסה שוב');
    }
  }

  async function handleDelete() {
    if (!company) return;
    if (!window.confirm(`למחוק את חברת ${company.name}?`)) return;

    try {
      await softDeleteCompany.mutateAsync(company.id);
      toast.success('החברה נמחקה');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  async function handleContactSubmit(values: ContactInput) {
    try {
      await upsertContact.mutateAsync({ id: selectedContact?.id ?? null, values });
      toast.success('איש הקשר נשמר');
      setSelectedContact(null);
      setContactView('list');
    } catch {
      toast.error('לא הצלחנו לשמור · נסה שוב');
    }
  }

  async function handleContactDelete(contact: CompanyContactRow) {
    if (!window.confirm(`למחוק את ${contact.full_name}?`)) return;

    try {
      await softDeleteContact.mutateAsync(contact.id);
      toast.success('איש הקשר נמחק');
      setSelectedContact(null);
      setContactView('list');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  function openContactForm(contact: CompanyContactRow | null) {
    setSelectedContact(contact);
    setContactView('form');
  }

  const companyFooter = (
    <>
      {isEditing ? (
        <button
          type="button"
          className={styles.btnDanger}
          disabled={companyBusy}
          onClick={() => {
            void handleDelete();
          }}
        >
          <Trash2 size={15} aria-hidden="true" />
          מחק
        </button>
      ) : null}
      <button
        type="button"
        className={styles.btnSecondary}
        disabled={companyBusy}
        onClick={onClose}
      >
        בטל
      </button>
      <button type="submit" form={COMPANY_FORM_ID} className={styles.btnPrimary} disabled={companyBusy}>
        שמור
      </button>
    </>
  );

  const contactFooter =
    contactView === 'form' ? (
      <>
        {selectedContact ? (
          <button
            type="button"
            className={styles.btnDanger}
            disabled={contactBusy}
            onClick={() => {
              void handleContactDelete(selectedContact);
            }}
          >
            <Trash2 size={15} aria-hidden="true" />
            מחק
          </button>
        ) : null}
        <button
          type="button"
          className={styles.btnSecondary}
          disabled={contactBusy}
          onClick={() => {
            setSelectedContact(null);
            setContactView('list');
          }}
        >
          בטל
        </button>
        <button type="submit" form={CONTACT_FORM_ID} className={styles.btnPrimary} disabled={contactBusy}>
          שמור
        </button>
      </>
    ) : undefined;

  const footer = tab === 'overview' ? companyFooter : tab === 'contacts' ? contactFooter : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={
        <>
          חברה
          {displayId ? <EntityIdBadge id={displayId} /> : null}
        </>
      }
      title={company?.name ?? (freshId ? 'איש קשר ראשון' : 'חברה חדשה')}
      ariaLabel={isEditing ? 'עריכת חברה' : 'חברה חדשה'}
      footer={footer}
    >
      {isEditing ? (
        <div className={styles.tabs} role="tablist" aria-label="פרטי חברה">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'overview'}
            className={cn(styles.tab, tab === 'overview' && styles.tabActive)}
            onClick={() => {
              setTab('overview');
            }}
          >
            סקירה
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'contacts'}
            className={cn(styles.tab, tab === 'contacts' && styles.tabActive)}
            onClick={() => {
              setTab('contacts');
              setContactView('list');
              setSelectedContact(null);
            }}
          >
            אנשי קשר ({contacts.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'deals'}
            className={cn(styles.tab, tab === 'deals' && styles.tabActive)}
            onClick={() => {
              setTab('deals');
            }}
          >
            דילים ({companyDeals.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'activity'}
            className={cn(styles.tab, tab === 'activity' && styles.tabActive)}
            onClick={() => {
              setTab('activity');
            }}
          >
            פעילות
          </button>
        </div>
      ) : null}

      {tab === 'overview' ? (
        <>
          {isEditing ? (
            <KpiStrip
              deals={companyDeals}
              contactsCount={contacts.length}
              customerSince={customerSince}
            />
          ) : null}
          <CompanyForm
            company={company}
            formId={COMPANY_FORM_ID}
            disabled={companyBusy}
            onSubmit={handleSubmit}
          />
        </>
      ) : null}

      {tab === 'contacts' && effectiveId ? (
        contactView === 'list' ? (
          <ContactsList
            contacts={contacts}
            isLoading={contactsLoading}
            isError={Boolean(contactsError)}
            onCreate={() => {
              openContactForm(null);
            }}
            onEdit={openContactForm}
          />
        ) : (
          <div>
            <div className={contactStyles.formHead}>
              <button
                type="button"
                className={contactStyles.backButton}
                onClick={() => {
                  setSelectedContact(null);
                  setContactView('list');
                }}
              >
                <ChevronRight size={15} aria-hidden="true" />
                חזרה לרשימה
              </button>
            </div>
            <ContactForm
              contact={selectedContact}
              formId={CONTACT_FORM_ID}
              disabled={contactBusy}
              onSubmit={handleContactSubmit}
            />
          </div>
        )
      ) : null}

      {tab === 'deals' && effectiveId ? <DealsList deals={companyDeals} /> : null}
      {tab === 'activity' && effectiveId ? (
        <ActivityFeed rows={companyActivity} isLoading={activityLoading} />
      ) : null}
    </Drawer>
  );
}
